/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import {
  ComponentItemFinderDialogConfig,
  componentItemFinderDialogRoute,
  ComponentItemFinderListItem,
} from '@gh/builder-shared';
import { ComponentItemFilterMode } from '@gh/builder-shared-data';
import { getAllNewSelectedEntities, State } from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { DialogService, NotificationService, yesAnswer, yesNoConfirmation } from '@gh/core-ui';
import { filter$, map$ } from '@gh/rx/operators';

import {
  ComponentDetails,
  componentItemFromFinder,
} from '../../resources/component-and-item-details.model';
import {
  ComponentDetailsItemsState,
  ComponentDetailsItemsStateEvent,
} from './component-details-items.state';

/**
 * Command service encapsulates all side effects mutating state of items tab
 */
@Injectable()
export class ComponentDetailsCommandService {
  constructor(private dialogService: DialogService,
              private notificationService: NotificationService,
              private itemsState: State<ComponentDetailsItemsState>) {

  }

  navigateToInvalid(): void {
    this.notificationService.error('msg_component_item_incorrect');
    this.itemsState.mutate({ type: ComponentDetailsItemsStateEvent.NavigateToInvalid });
  }

  updateItemField(id: ID, name: string, value: any): void {
    this.itemsState.mutate({ type: ComponentDetailsItemsStateEvent.SetItemField, id, name, value });
  }

  addItem(): void {
    this.dialogService.openWithInput(componentItemFinderDialogRoute, <ComponentItemFinderDialogConfig>{
      filterMode: ComponentItemFilterMode.Items,
      disableFilterMode: true,
      multiselection: true,
      preselected: [],
    }).afterClosed().pipe(
      filter$(Boolean),
      map$(getAllNewSelectedEntities))
      .subscribe((items: ComponentItemFinderListItem[]) => {
        this.itemsState.mutate({
          type: ComponentDetailsItemsStateEvent.AddItems,
          items: items.map(componentItemFromFinder),
        });
      });
  }

  removeItem(id: ID): void {
    this.dialogService
      .confirmation(yesNoConfirmation('msg_component_item_delete')).pipe(
      filter$(yesAnswer))
      .subscribe(() => {
        this.itemsState.mutate({ type: ComponentDetailsItemsStateEvent.RemoveItem, id });
      });
  }

  removeItems(ids: ID[]): void {
    ids.forEach((id) => {
      this.itemsState.mutate({ type: ComponentDetailsItemsStateEvent.RemoveItem, id });
    });
  }

  load(details: ComponentDetails): void {
    this.itemsState.mutate({ type: ComponentDetailsItemsStateEvent.Load, data: details });
  }

  merge(details: ComponentDetails): void {
    this.itemsState.mutate({ type: ComponentDetailsItemsStateEvent.Merge, data: details });
  }

  markAsPristine(): void {
    this.itemsState.mutate({ type: ComponentDetailsItemsStateEvent.MarkAsPristine });
  }
}
